import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, X, Send } from "lucide-react";
import { toast } from "sonner";

const ADMIN_SUGGESTIONS = [
  "Summarize team efficiency this month.",
  "Which projects are at burnout risk?",
  "Who has the lowest utilization in the last 4 weeks?",
  "Which client is most profitable?",
];

const MEMBER_SUGGESTIONS = [
  "What's on my plate today?",
  "Am I tracking above or below benchmark this month?",
  "Which of my tasks are overdue?",
  "Where am I logging the most hours this month?",
];

function getOrCreateSessionId() {
  let id = localStorage.getItem("studio_ai_session");
  if (!id) {
    id = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("studio_ai_session", id);
  }
  return id;
}

export default function AIChat() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(getOrCreateSessionId);
  const scrollRef = useRef(null);

  const canUse = !!user;
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const SUGGESTIONS = isAdmin ? ADMIN_SUGGESTIONS : MEMBER_SUGGESTIONS;

  useEffect(() => {
    const onReset = () => {
      const newId = localStorage.getItem("studio_ai_session");
      if (newId) setSessionId(newId);
    };
    window.addEventListener("studio:ai-session-reset", onReset);
    return () => window.removeEventListener("studio:ai-session-reset", onReset);
  }, []);

  useEffect(() => {
    if (!canUse || !open || messages.length > 0) return;
    api.get(`/ai/chat/${sessionId}`).then(r => setMessages(r.data || [])).catch(() => {});
  }, [open, sessionId, canUse, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  if (!canUse) return null;

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message) return;
    setInput("");
    setMessages(prev => [...prev, { id: `local-${Date.now()}`, role: "user", content: message, created_at: new Date().toISOString() }]);
    setLoading(true);
    try {
      const r = await api.post("/ai/chat", { session_id: sessionId, message });
      setMessages(prev => [...prev, r.data]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chat failed");
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    if (!confirm("Clear chat history?")) return;
    const newId = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("studio_ai_session", newId);
    setMessages([]);
    // We deliberately don't reload — just rotate session id for next message
    window.dispatchEvent(new Event("studio:ai-session-reset"));
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-chat-launcher"
          className="fixed z-50 bottom-20 right-6 w-14 h-14 bg-neutral-900 text-white rounded-sm shadow-lg hover:bg-neutral-800 transition-colors grid place-items-center group"
          aria-label="Open AI assistant"
        >
          <Sparkles size={20} className="group-hover:scale-110 transition-transform" />
        </button>
      )}

      {/* Slideover panel */}
      {open && (
        <div className="fixed z-50 inset-y-0 right-0 w-full sm:w-[440px] bg-white border-l border-neutral-200 shadow-2xl flex flex-col" data-testid="ai-chat-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-neutral-900 grid place-items-center"><Sparkles size={14} className="text-white" /></div>
              <div>
                <div className="font-display font-bold text-sm">{isAdmin ? "Studio AI" : "Your AI coach"}</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Claude Sonnet 4.5</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={reset} className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-neutral-900 px-2 py-1" data-testid="ai-chat-reset">Reset</button>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-neutral-100 rounded-sm" data-testid="ai-chat-close" aria-label="Close"><X size={18} /></button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {messages.length === 0 && !loading && (
              <div className="text-center pt-8" data-testid="ai-chat-empty">
                <div className="font-display text-xl font-bold mb-1">Ask anything.</div>
                <div className="text-sm text-neutral-500 mb-6">
                  {isAdmin
                    ? "I have live access to your projects, hours, margins, and team."
                    : "I see your assigned tasks, hours and benchmark efficiency only — not teammate or studio-wide data."}
                </div>
                <div className="space-y-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="w-full text-left text-sm border border-neutral-200 hover:border-neutral-400 hover:bg-neutral-50 p-3 transition-colors"
                      data-testid={`ai-suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map(m => (
              <div key={m.id} className={`text-sm leading-relaxed ${m.role === "user" ? "text-right" : ""}`} data-testid={`ai-msg-${m.id}`}>
                <div className={`inline-block max-w-[85%] px-3 py-2 rounded-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-neutral-900 text-white" : "bg-neutral-50 border border-neutral-200 text-neutral-900"
                }`}>{m.content}</div>
              </div>
            ))}
            {loading && (
              <div className="text-sm" data-testid="ai-loading">
                <div className="inline-flex items-center gap-2 px-3 py-2 bg-neutral-50 border border-neutral-200 text-neutral-500">
                  <span className="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-pulse"></span>
                  <span className="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-pulse" style={{ animationDelay: "0.15s" }}></span>
                  <span className="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-pulse" style={{ animationDelay: "0.3s" }}></span>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-neutral-200 p-3 flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={isAdmin ? "Ask about efficiency, margins, workload…" : "Ask about your tasks, hours, benchmark…"}
              className="flex-1 border border-neutral-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-neutral-900"
              data-testid="ai-chat-input"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-neutral-900 text-white p-2 rounded-sm hover:bg-neutral-800 disabled:opacity-40"
              data-testid="ai-chat-send"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
